import fixture from "./fixtures/healthyHabitsPreview.json";
import { readJson, remove, writeJson } from "./storage";
import { attemptSyncKey } from "./syncKey";

// Development-only stand-in for Directus, kept on this device. It mirrors the
// live repository's contract so the learner journey can be exercised before
// the release seam and learner permissions exist. It is never used in live
// mode and nothing it stores is production-backed.

const KEY = "preview-db";

const empty = () => ({
  release: null,
  attempts: {},
  evidence: [],
  files: [],
});

const load = () => ({ ...empty(), ...readJson(KEY, {}) });
const save = (db) => writeJson(KEY, db);

// The one learner a preview tablet stands for.
const previewKey = (deliveryId, activityId) =>
  attemptSyncKey({ deliveryId, learnerId: "preview-learner", activityId });

let nextId = 0;
const id = (prefix) => `${prefix}-${Date.now().toString(36)}-${(nextId += 1)}`;

const previewRepository = {
  isSignedIn: async () => true,
  provision: async () => ({ provisioned: true }),
  signOut: async () => {},
  isEnrolled: async () => true,

  currentRelease: async () => load().release,

  readActivity: async () => fixture,

  findAttempt: async ({ deliveryId, activityId }) =>
    load().attempts[previewKey(deliveryId, activityId)] || null,

  async createAttempt({ deliveryId, activityId, currentStep }) {
    const syncKey = previewKey(deliveryId, activityId);
    const db = load();
    if (db.attempts[syncKey]) {
      const conflict = new Error("sync_key already exists");
      conflict.response = { status: 400 };
      throw conflict;
    }
    const attempt = {
      id: id("attempt"),
      syncKey,
      completed: false,
      currentStep,
      responses: {},
      completedAt: null,
    };
    db.attempts[syncKey] = attempt;
    save(db);
    return attempt;
  },

  async updateAttempt(attemptId, { responses, currentStep, completedAt }) {
    const db = load();
    const attempt = Object.values(db.attempts).find((a) => a.id === attemptId);
    Object.assign(attempt, { responses, currentStep });
    if (completedAt) Object.assign(attempt, { completed: true, completedAt });
    save(db);
    return attempt;
  },

  // Same contract as the gateway: one file and one evidence record per
  // attempt and step, however often it is sent.
  async saveDrawing({ attempt, stepId, blob }) {
    const db = load();
    const linked = db.evidence.some(
      (e) => e.attemptId === attempt.id && e.stepId === stepId
    );
    if (!linked) {
      const file = {
        id: id("file"),
        title: `learner-drawing ${attempt.syncKey} ${stepId}`,
        size: blob.size,
        type: blob.type,
      };
      db.files.push(file);
      db.evidence.push({
        id: id("evidence"),
        attemptId: attempt.id,
        stepId,
        evidenceType: "drawing",
        fileId: file.id,
        capturedAt: new Date().toISOString(),
      });
      save(db);
    }
    return { saved: true };
  },
};

// The development-only stand-in for "Release learner activity" in the
// Teacher App. Releasing twice keeps the first release (handoff §16).
export const previewControls = {
  release() {
    const db = load();
    if (!db.release) {
      db.release = {
        deliveryId: id("delivery"),
        activityId: fixture.id,
        releasedAt: new Date().toISOString(),
      };
      save(db);
    }
  },
  // A fresh delivery: the next release gets a new attempt.
  newDelivery() {
    const db = load();
    db.release = null;
    save(db);
  },
  reset: () => remove(KEY),
  snapshot: load,
};

export default previewRepository;
